import cn from 'classnames';
import {
  useState,
  useEffect,
  useRef
} from 'react';
import PropTypes from 'prop-types';

import offsetTopDocument from '../../util/offsetTopDocument.js';

export default function LdsJumpMenu({
  ariaLabel = 'in-page navigation',
  menu,
  stickyCeiling = undefined,
  stickyFloor = undefined,
  topPadding = 0,
  leftPadding = 16,
  className = '',
  ...rest
}) {
  const [activeAnchor, setActiveAnchor] = useState('');
  const [sticky, setSticky] = useState(false);
  const [jumpMenuStyle, setJumpMenuStyle] = useState({
    top: `${topPadding}px`,
    left: `${leftPadding}px`,
    width: '100%',
  });

  const navRef = useRef(null);

  const jumpOffset = 16;

  const getStickyTop = () => {
    const stickyElement = document.querySelector('.lds-sticky');
    let offset = 0;
    if (stickyElement) offset += stickyElement.offsetHeight;
    return offset;
  };

  const getStickyCeilingY = () => {
    const windowY = window.pageYOffset;
    const stickyCeilingElm = document.getElementById(stickyCeiling);
    return stickyCeilingElm.getBoundingClientRect().top + windowY;
  };

  const getStickyFloorY = () => {
    const windowY = window.pageYOffset;
    const stickyFloorElm = document.getElementById(stickyFloor);
    return stickyFloorElm.getBoundingClientRect().top + windowY - getStickyTop();
  };

  const getNavHeight = () => {
    return navRef.current.getBoundingClientRect().height;
  };

  const getNavOffsetLeft = () => {
    return navRef.current.getBoundingClientRect().left;
  };

  const isBelowTopAnchor = () => {
    const windowY = window.pageYOffset;
    const anchorTopY = getStickyCeilingY();
    return windowY > (anchorTopY - topPadding);
  };

  const isAboveBottomAnchor = () => {
    const windowY = window.pageYOffset;
    const anchorBottomY = getStickyFloorY();
    return windowY + getNavHeight() < anchorBottomY;
  };

  const isSticky = () => {
    return isBelowTopAnchor() && isAboveBottomAnchor();
  };

  const getNavOffsetTop = () => {
    const offset = getStickyFloorY()
      - getStickyCeilingY()
      - getNavHeight()
      - getStickyTop();
    return isBelowTopAnchor() ? offset : topPadding;
  };

  const handleUpdateSticky = ({ stickyValue } = {}) => {
    if (stickyValue) {
      const offsetLeft = getNavOffsetLeft();
      const offsetWidth = navRef.current.offsetWidth + 1;
      setSticky(true);
      setJumpMenuStyle({
        top: `${topPadding + getStickyTop()}px`,
        left: `${offsetLeft}px`,
        width: `${offsetWidth}px`,
        maxHeight: `${window.innerHeight - (topPadding * 2) - getStickyTop()}px`,
      });
    } else {
      const offsetTop = getNavOffsetTop();
      setSticky(false);
      setJumpMenuStyle({
        left: `${leftPadding}px`,
        top: `${offsetTop}px`,
        width: '100%',
        maxHeight: `${window.innerHeight - (topPadding * 2) - getStickyTop()}px`,
      });
    }
  };

  const checkSticky = () => {
    if (isSticky()) {
      handleUpdateSticky({ stickyValue: true });
    } else if (!isSticky()) {
      handleUpdateSticky({ stickyValue: false });
    }
  };

  const getPageAnchors = () => {
    const anchors = menu.map((item) => {
      const element = document.getElementById(item.anchor);
      if (!element) {
        /* eslint-disable-next-line no-console */
        console.error(`LDS ERROR [Unexpected Failure]: Jump menu anchors must match elements existing in the
        DOM. Check the anchor for a jump menu item with the text "${item.text}"`, '\n>>> in LdsJumpMenu');
      }
      return {
        anchor: item.anchor,
        element,
      };
    });
    return anchors;
  };

  const setUrlHash = (anchor) => {
    const url = new URL(window.location);
    url.hash = anchor;
    history.pushState({}, '', url);
  };

  const jumpToAnchor = (e, anchor) => {
    e.preventDefault();
    const pageAnchors = getPageAnchors();
    const { element } = pageAnchors.find((item) => item.anchor === anchor);
    if (!element) return;
    window.scrollTo({ top: offsetTopDocument(element) - parseInt(jumpOffset, 10) - getStickyTop(), behavior: 'smooth' });
    setUrlHash(anchor);
    setTimeout(() => document.getElementById(anchor).focus({ focusVisible: true }), 500);
  };

  function getActiveAnchor() {
    const pageAnchors = getPageAnchors();
    return [...pageAnchors].reverse()
      .reduce((active, item) => {
        if (active !== false || !item.element) return active;
        const offsetTop = offsetTopDocument(item.element)
          - 1
          - parseInt(jumpOffset, 10)
          - getStickyTop();
        const windowScroll = document.documentElement.scrollTop || window.pageYOffset;
        return offsetTop < windowScroll ? item : active;
      }, false);
  }

  const onPageScroll = () => {
    const actAnchor = getActiveAnchor();
    if (actAnchor) {
      setActiveAnchor(actAnchor.anchor);
      setUrlHash(actAnchor.anchor);
    }
    checkSticky();
  };

  useEffect(() => {
    window.addEventListener('scroll', onPageScroll);
    return () => window.removeEventListener('scroll', onPageScroll);
  }, []);

  return (
    <div>
      <nav
        aria-label={ariaLabel}
        ref={navRef}
        className={cn(
          'lds-jump-menu',
          {
            'lds-jump-menu-sticky': sticky,
          },
          className
        )}
        style={jumpMenuStyle}
        {...rest}
      >
        <ul className="lds-jump-menu-list">
          {
            menu.map((menuItem, idx) => (
              <li
                key={idx}
                className={cn(
                  'lds-jump-menu-list-item',
                  {
                    'lds-jump-menu-list-item-active': activeAnchor && menuItem.anchor === activeAnchor,
                  }
                )}
              >
                <a
                  href={`#${menuItem.anchor}`}
                  onClick={(e) => jumpToAnchor(e, menuItem.anchor)}
                >
                  {menuItem.text}
                </a>
              </li>
            ))
          }
        </ul>
      </nav>
    </div>
  );
}

LdsJumpMenu.propTypes = {
  ariaLabel: PropTypes.string,
  menu: PropTypes.array.isRequired,
  stickyCeiling: PropTypes.string.isRequired,
  stickyFloor: PropTypes.string.isRequired,
  topPadding: PropTypes.number,
  leftPadding: PropTypes.number,
};
